import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing16, Spacing24, Spacing40 } from 'autogen/design-tokens/tokens';
import {
    Description,
    Dismiss,
    Headline
} from 'autogen/translation-keys/trans-website-productreview-reviewreport-reviewreportquickview';
import { Button } from 'components/Button';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { Typography } from 'components/generic/typography';
import QuickView, { QuickViewWithAbsoluteHeader, useQuickView } from 'components/quickView';
import ActionFooter from 'components/quickView/components/ActionFooter';
import format from 'helpers/translations';
import type { IResponsiveImageModel } from 'shared/interfaces/responsiveImage';
import { breakpoint, sizes } from 'variables';

// #region Styled
const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ScrollContainer = styled.div`
    flex: 0 1 100%;
    overflow-x: hidden;
    overflow-y: auto;
`;

const StyledPicture = styled(PictureDeprecated)`
    width: 100%;
    height: auto;
`;

const Top = styled.div`
    padding: ${Spacing40};
`;

const Title = styled(Typography.Headline)`
    display: block;
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        margin-bottom: ${Spacing24};
    }
`;

const Paragraph = styled(Typography.Body)`
    display: block;
    margin: 0 auto;
`;
// #endregion Styled

export default function AIExplainerQuickview({ imageData }: { imageData?: IResponsiveImageModel }) {
    const { t } = useTranslation();
    const { toggle } = useQuickView('AIExplainerQuickview');

    const footerRenderer = React.useMemo(
        () => (
            <ActionFooter>
                <Button size="lg" onClick={() => toggle(false)}>
                    {t(Dismiss)}
                </Button>
            </ActionFooter>
        ),
        [t, toggle]
    );

    const content = (
        <Content>
            <ScrollContainer>
                {imageData && <StyledPicture {...imageData} />}
                <Top>
                    <Title size="L">{t(Headline)}</Title>
                    <Paragraph>{format(t(Description))}</Paragraph>
                </Top>
            </ScrollContainer>
        </Content>
    );

    return (
        <QuickViewWithAbsoluteHeader
            quickViewId="AIExplainerQuickview"
            position="right"
            footerRenderer={() => footerRenderer}
        >
            {content}
        </QuickViewWithAbsoluteHeader>
    );
}
